/**
 * InputColumnChips — выбор evidence columns для matching (§9 / columnDenylist).
 *
 * Block M1a-FE (TZ 2026-07-29). Берёт `original_columns` из dataset preview,
 * прогоняет через columnDenylist (internal meta filter — UX-J10), рендерит
 * compact full-width chip-row с ×.
 *
 * matching-v2 §C: HTML5 native drag&drop reorder — чипы можно перетаскивать,
 * порядок сохраняется в store (persist) и важен как подсказка приоритета
 * evidence-колонок. Native dnd (draggable attr + dragstart/dragover/drop),
 * без внешних библиотек.
 *
 * Columns picker для matching НЕ сегрегированный (orig/enr/gen): в M1a мы
 * работаем с raw слоем как с input (§8.1 / DM-15 — generated_content не используется).
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Columns, GripVertical } from 'lucide-react';

import { useMatchingStore } from '@/store/matchingStore';
import { useDatasetPreview } from '@/hooks/useFiles';
import { filterUserVisibleColumns } from '@/lib/columnDenylist';
import { cn } from '@/lib/utils';

export interface InputColumnChipsProps {
  datasetId: string | null;
}

export function InputColumnChips({ datasetId }: InputColumnChipsProps) {
  const { t } = useTranslation();
  const { data: preview } = useDatasetPreview(datasetId);
  const selected = useMatchingStore((s) => s.selectedInputColumns);
  const toggleColumn = useMatchingStore((s) => s.toggleInputColumn);
  const setSelected = useMatchingStore((s) => s.setSelectedInputColumns);
  const inputColumnOrder = useMatchingStore((s) => s.inputColumnOrder);
  const reorderInputColumn = useMatchingStore(
    (s) => s.reorderInputColumn,
  );
  /** Currently dragged chip key (html5 dnd state). */
  const [dragKey, setDragKey] = useState<string | null>(null);

  const allColumns = useMemo<string[]>(() => {
    const rawMeta = preview?.dataset?.raw_metadata as
      | { original_columns?: string[] }
      | undefined;
    const cols = rawMeta?.original_columns ?? [];
    return filterUserVisibleColumns(cols);
  }, [preview]);

  /** Display order: persisted drag order first, then any unseen columns. */
  const ordered = useMemo<string[]>(() => {
    const seen = new Set(inputColumnOrder);
    const tail = allColumns.filter((c) => !seen.has(c));
    const head = inputColumnOrder.filter((c) => allColumns.includes(c));
    return [...head, ...tail];
  }, [allColumns, inputColumnOrder]);

  // Reset selection если датасет сменился и выбранных колонок больше нет в датасете.
  useEffect(() => {
    if (selected.length === 0 || allColumns.length === 0) return;
    const available = new Set(allColumns);
    const stillThere = selected.filter((c) => available.has(c));
    if (stillThere.length !== selected.length) {
      setSelected(stillThere);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allColumns.join('|')]);

  return (
    <section className="card space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-400 flex items-center gap-2">
          <Columns className="w-4 h-4" />
          {t('process.matching.results.inputColumns.title', {
            defaultValue: 'Input columns',
          })}
        </h2>
        {datasetId && (
          <span className="text-xs text-surface-500">
            {t('process.matching.results.inputColumns.selected', {
              defaultValue: '{n}/{total} selected',
              n: selected.length,
              total: allColumns.length,
            })}
          </span>
        )}
      </div>

      {!datasetId ? (
        <p className="text-xs text-surface-500 italic">
          {t('process.matching.results.inputColumns.loadEmpty', {
            defaultValue: 'Pick a dataset to load column candidates.',
          })}
        </p>
      ) : allColumns.length === 0 ? (
        <p className="text-xs text-surface-500 italic">
          {t('process.matching.results.inputColumns.noColumns', {
            defaultValue: 'No columns available in this dataset.',
          })}
        </p>
      ) : (
        <div
          className="flex flex-wrap gap-1.5"
          onDragEnd={() => setDragKey(null)}
        >
          {ordered.map((col) => {
            const active = selected.includes(col);
            return (
              <button
                key={col}
                type="button"
                draggable
                onClick={() => toggleColumn(col)}
                onDragStart={(e) => {
                  setDragKey(col);
                  e.dataTransfer.effectAllowed = 'move';
                  try {
                    e.dataTransfer.setData('text/plain', col);
                  } catch {
                    /* dataTransfer unavailable in tests — safe to skip */
                  }
                }}
                onDragOver={(e) => {
                  if (dragKey && dragKey !== col) e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragKey && dragKey !== col) reorderInputColumn(dragKey, col);
                  setDragKey(null);
                }}
                className={cn(
                  'flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors border',
                  active
                    ? 'border-primary-500/50 bg-primary-500/10 text-primary-200'
                    : 'border-surface-700 bg-surface-900 text-surface-400 hover:border-surface-500 hover:text-surface-200',
                  dragKey === col && 'opacity-50',
                )}
                aria-pressed={active}
                aria-label={t('process.matching.results.inputColumns.dragHint', {
                  defaultValue: 'Drag to reorder columns',
                })}
                title={t('process.matching.results.inputColumns.dragHint', {
                  defaultValue: 'Drag to reorder columns',
                })}
              >
                <GripVertical className="w-3 h-3 shrink-0 opacity-60" aria-hidden />
                <span>{col}</span>
                {active && <span aria-hidden>×</span>}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}